import type { UserPreferenceSource } from "@verbleif/lib/src/composables/Api/Type/UserPreferences/user_preferences.interface";
import type { ResourceKeyFactory } from "../Query/resourceKeys";
import type {
  InheritedScopedViewItem,
  PositionWrite,
  ScopedViewItem,
  ViewFormValues,
  ViewLevelContext,
  ViewsCollectionRequest,
  ViewsSettingsKind,
  ViewsSettingsLevel,
  ViewsSettingsRow,
  ViewWriteMode,
} from "./types";
import { iriToId } from "@verbleif/lib/src/composables/Utils/iri.helpers";
import { resourceKeys } from "../Query/resourceKeys";
import { REPORT_VIEW_SIDEBAR_LABEL_KEYS } from "./reportViewSidebarLabels";
import { resolveViewSidebarLabel } from "./resolveViewSidebarLabel";
import { TASK_VIEW_SIDEBAR_LABEL_KEYS } from "./taskViewSidebarLabels";

export type {
  InheritedScopedViewItem,
  PositionWrite,
  ScopedViewItem,
  ViewFormValues,
  ViewLevelContext,
  ViewsCollectionRequest,
  ViewsSettingsKind,
  ViewsSettingsLevel,
  ViewsSettingsRow,
  ViewWriteMode,
} from "./types";

export const VIEW_LEVEL_ORDER: readonly ViewsSettingsLevel[] = ["global", "client", "location", "role", "user"];
export const VIEW_KEY_PATTERN = /^[a-z0-9_]+$/;
export const VIEW_POSITION_STEP = 1000;
export const VIEW_ROW_DRAG_TYPE = "view-row";
export const VIEW_POSITION_INT32_MAX = 2_147_483_647;

export type ViewInheritableField = "name" | "avatarIconIdentifier" | "avatarColor" | "definition";

export const VIEW_INHERITABLE_FIELDS: readonly ViewInheritableField[] = [
  "name",
  "avatarIconIdentifier",
  "avatarColor",
  "definition",
];

function resourceSegment(kind: ViewsSettingsKind): string {
  return kind === "tasks" ? "task_views" : "report_views";
}

function inheritedSegment(kind: ViewsSettingsKind): string {
  return kind === "tasks" ? "inherited_task_views" : "inherited_report_views";
}

export function viewsItemsUrl(kind: ViewsSettingsKind): string {
  return `/api/${resourceSegment(kind)}`;
}

/**
 * Location IRI for entity filter pickers (properties, sorts, users).
 * Global / client: always null (entities are location-bound; API rejects a location on those rows).
 * User: the location the user works in (`locationIri`). Their own rows have no other place to pick
 * entities, and choices from other locations stay listed as hidden selections.
 * Role: only when the PTG itself is tied to a location (`roleLocationIri`).
 * Location: the view's location IRI.
 */
export function resolveEntityLocationIri(
  level: ViewsSettingsLevel,
  options: {
    locationIri?: string | null
    roleLocationIri?: string | null
  } = {},
): string | null {
  if (level === "global" || level === "client") {
    return null;
  }
  if (level === "role") {
    const roleLocation = options.roleLocationIri ?? null;
    return roleLocation && roleLocation !== "" ? roleLocation : null;
  }
  const location = options.locationIri ?? null;
  return location && location !== "" ? location : null;
}

export function inheritedViewsUrl(
  kind: ViewsSettingsKind,
  level: ViewsSettingsLevel,
  context: ViewLevelContext,
): string | null {
  if (level === "global") {
    return null;
  }
  const segment = inheritedSegment(kind);
  switch (level) {
    case "client":
      return context.clientIri ? `${context.clientIri}/${segment}` : null;
    case "location":
      return context.locationIri ? `${context.locationIri}/${segment}` : null;
    case "role": {
      const ptg = context.permissionTemplateGroupIri;
      if (!ptg) {
        return null;
      }
      const roleLocation = context.roleLocationIri;
      if (roleLocation) {
        return `${ptg}/locations/${iriToId(roleLocation)}/${segment}`;
      }
      return `${ptg}/${segment}`;
    }
    case "user": {
      const userIri = context.userIri;
      const location = context.userLocationIri;
      if (!userIri || !location) {
        return null;
      }
      return `${userIri}/locations/${iriToId(location)}/${segment}`;
    }
    default:
      return null;
  }
}

export function viewsCollectionRequest(
  kind: ViewsSettingsKind,
  level: ViewsSettingsLevel,
  context: ViewLevelContext,
): ViewsCollectionRequest | null {
  const params = new URLSearchParams();
  if (level === "global") {
    params.set("scope", "global");
    return { url: viewsItemsUrl(kind), params };
  }

  const parentIri = viewLevelParentIri(level, context);
  if (!parentIri) {
    return null;
  }
  return { url: `${parentIri}/${resourceSegment(kind)}`, params };
}

export function viewLevelParentIri(level: ViewsSettingsLevel, context: ViewLevelContext): string | null {
  switch (level) {
    case "client":
      return context.clientIri;
    case "location":
      return context.locationIri;
    case "role":
      return context.permissionTemplateGroupIri;
    case "user":
      return context.userIri;
    default:
      return null;
  }
}

export function viewsScopeRelation(level: ViewsSettingsLevel, context: ViewLevelContext): Record<string, string> | null {
  if (level === "global") {
    return {};
  }
  switch (level) {
    case "client": {
      if (!context.clientIri) {
        return null;
      }
      return { client: context.clientIri };
    }
    case "location": {
      const client = context.locationClientIri ?? context.clientIri;
      if (!client || !context.locationIri) {
        return null;
      }
      return { client, location: context.locationIri };
    }
    case "role": {
      const client = context.roleClientIri ?? context.clientIri;
      if (!client || !context.permissionTemplateGroupIri) {
        return null;
      }
      return { client, permissionTemplateGroup: context.permissionTemplateGroupIri };
    }
    case "user": {
      if (!context.userIri) {
        return null;
      }
      return { relatedUser: context.userIri };
    }
    default:
      return null;
  }
}

export function slugifyViewKey(name: string): string {
  return name
    .normalize("NFD")
    .replace(/[\u0300-\u036F]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 64);
}

export function generateUniqueViewKey(name: string, existingKeys: ReadonlySet<string> | readonly string[]): string {
  const taken = new Set(existingKeys);
  const base = slugifyViewKey(name);
  if (!base) {
    throw new Error("View name must produce a valid key");
  }
  if (!taken.has(base)) {
    return base;
  }
  for (let suffix = 2; suffix < 10_000; suffix += 1) {
    const suffixText = `_${suffix}`;
    const candidate = `${base.slice(0, Math.max(0, 64 - suffixText.length))}${suffixText}`;
    if (!taken.has(candidate)) {
      return candidate;
    }
  }
  throw new Error("Could not generate a unique view key");
}

function rowSortPosition(position: number | null | undefined): number {
  if (position == null || !Number.isFinite(position)) {
    return Number.MAX_SAFE_INTEGER;
  }
  return position;
}

interface OrderedViewRow {
  key: string
  position: number
  fixed?: boolean
}

/** Fixed built-ins come first whatever their position, like the API orders them. */
export function sortViewRows<T extends OrderedViewRow>(rows: T[]): T[] {
  return [...rows].sort((a, b) =>
    Number(b.fixed === true) - Number(a.fixed === true)
    || a.position - b.position
    || a.key.localeCompare(b.key));
}

/** Rows whose place in the list can change; a fixed built-in keeps its place at the top. */
export function movableViewRows<T extends OrderedViewRow>(rows: readonly T[]): T[] {
  return sortViewRows(rows.filter(row => row.fixed !== true));
}

export function buildViewsRows(
  inherited: InheritedScopedViewItem[],
  own: ScopedViewItem[],
  level: ViewsSettingsLevel,
): ViewsSettingsRow[] {
  const inheritedByKey = new Map(inherited.map(item => [item.key, item]));
  const ownByKey = new Map(own.map(row => [row.key, row]));
  const keys = new Set([...inheritedByKey.keys(), ...ownByKey.keys()]);

  const rows: ViewsSettingsRow[] = [];
  for (const key of keys) {
    const inheritedItem = inheritedByKey.get(key) ?? null;
    const ownRow = ownByKey.get(key) ?? null;
    const hasAncestor = inheritedByKey.has(key);
    rows.push({
      key,
      inherited: inheritedItem,
      own: ownRow,
      name: ownRow?.name ?? inheritedItem?.name ?? null,
      translationKey: ownRow?.translationKey ?? inheritedItem?.translationKey ?? null,
      icon: ownRow?.avatarIconIdentifier ?? inheritedItem?.avatarIconIdentifier ?? null,
      color: ownRow?.avatarColor ?? inheritedItem?.avatarColor ?? null,
      enabled: ownRow?.enabled ?? inheritedItem?.enabled ?? true,
      position: rowSortPosition(ownRow?.position ?? inheritedItem?.position ?? null),
      fixed: ownRow?.fixed === true || inheritedItem?.fixed === true,
      hasAncestor,
      source: ownRow ? level : inheritedSourceForLevel(inheritedItem, level),
    });
  }
  return sortViewRows(rows);
}

export function nextViewPosition(rows: readonly { position: number }[]): number {
  const positions = rows
    .map(row => row.position)
    .filter(position => Number.isFinite(position) && position < Number.MAX_SAFE_INTEGER);
  if (positions.length === 0) {
    return VIEW_POSITION_STEP;
  }
  const next = Math.max(...positions) + VIEW_POSITION_STEP;
  if (next > VIEW_POSITION_INT32_MAX) {
    return VIEW_POSITION_STEP;
  }
  return next;
}

function positionBetween(
  previous: { position: number } | undefined,
  next: { position: number } | undefined,
): number | null {
  const candidate = unboundedPositionBetween(previous, next);
  if (candidate === null || candidate > VIEW_POSITION_INT32_MAX) {
    return null;
  }
  return candidate;
}

function unboundedPositionBetween(
  previous: { position: number } | undefined,
  next: { position: number } | undefined,
): number | null {
  if (previous && next) {
    if (next.position - previous.position <= 1) {
      return null;
    }
    const candidate = Math.floor((previous.position + next.position) / 2);
    if (candidate <= previous.position || candidate >= next.position) {
      return null;
    }
    return candidate;
  }
  if (previous) {
    return previous.position + VIEW_POSITION_STEP;
  }
  if (next) {
    if (next.position <= 1) {
      return null;
    }
    return Math.max(1, Math.floor(next.position / 2));
  }
  return VIEW_POSITION_STEP;
}

function renumberOrderedRows(ordered: readonly { key: string, position: number }[]): PositionWrite[] {
  const writes: PositionWrite[] = [];
  let position = VIEW_POSITION_STEP;
  for (const row of ordered) {
    if (row.position !== position) {
      writes.push({ key: row.key, position });
    }
    position += VIEW_POSITION_STEP;
    if (position > VIEW_POSITION_INT32_MAX) {
      position = VIEW_POSITION_STEP;
    }
  }
  return writes;
}

export function planReorder(
  rows: readonly OrderedViewRow[],
  draggedKey: string,
  targetKey: string,
  edge: "top" | "bottom",
): PositionWrite[] {
  if (draggedKey === targetKey) {
    return [];
  }
  const ordered = movableViewRows(rows);
  const dragged = ordered.find(row => row.key === draggedKey);
  const target = ordered.find(row => row.key === targetKey);
  if (!dragged || !target) {
    return [];
  }

  const without = ordered.filter(row => row.key !== draggedKey);
  let insertIndex = without.findIndex(row => row.key === targetKey);
  if (edge === "bottom") {
    insertIndex += 1;
  }

  const previous = without[insertIndex - 1];
  const next = without[insertIndex];
  const currentIndex = ordered.findIndex(row => row.key === draggedKey);
  const unchanged = ordered[currentIndex - 1]?.key === previous?.key && ordered[currentIndex + 1]?.key === next?.key;
  if (unchanged) {
    return [];
  }

  const candidate = positionBetween(previous, next);
  const collides = candidate !== null && ordered.some(row => row.key !== draggedKey && row.position === candidate);
  if (candidate !== null && !collides) {
    return [{ key: draggedKey, position: candidate }];
  }

  const reordered = [
    ...without.slice(0, insertIndex),
    dragged,
    ...without.slice(insertIndex),
  ];
  return renumberOrderedRows(reordered);
}

export function planAdjacentMove(
  rows: readonly OrderedViewRow[],
  key: string,
  direction: "up" | "down",
): PositionWrite[] {
  const ordered = movableViewRows(rows);
  const index = ordered.findIndex(row => row.key === key);
  if (index === -1) {
    return [];
  }
  const neighbour = ordered[direction === "up" ? index - 1 : index + 1];
  if (!neighbour) {
    return [];
  }
  return planReorder(ordered, key, neighbour.key, direction === "up" ? "top" : "bottom");
}

function normaliseFieldValue(_field: ViewInheritableField, value: unknown): unknown {
  if (value === "" || value === undefined) {
    return null;
  }
  return value;
}

export function buildViewWritePayload(
  values: ViewFormValues,
  isFieldDirty: (field: ViewInheritableField) => boolean,
  mode: ViewWriteMode,
): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  if (mode !== "patch") {
    payload.key = values.key;
  }
  for (const field of VIEW_INHERITABLE_FIELDS) {
    const dirty = isFieldDirty(field);
    if (mode === "patch" && !dirty) {
      continue;
    }
    if (mode === "override" && !dirty) {
      payload[field] = null;
      continue;
    }
    payload[field] = normaliseFieldValue(field, values[field]);
  }
  return payload;
}

function stableSerialise(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map(stableSerialise).join(",")}]`;
  }
  if (value !== null && typeof value === "object") {
    const record = value as Record<string, unknown>;
    return `{${Object.keys(record).sort().map(key => `${JSON.stringify(key)}:${stableSerialise(record[key])}`).join(",")}}`;
  }
  return JSON.stringify(value ?? null);
}

export function sameViewFieldValue(left: unknown, right: unknown): boolean {
  return stableSerialise(left) === stableSerialise(right);
}

export type ViewInheritedValues = Record<ViewInheritableField, unknown>;

/**
 * Write for a key that exists on a lower level. The form shows the effective values, so a field
 * that still equals what the level inherits is sent as `null` and keeps following the ancestor.
 */
export function buildOverrideWritePayload(
  values: ViewFormValues,
  inherited: ViewInheritedValues,
): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  for (const field of VIEW_INHERITABLE_FIELDS) {
    const value = normaliseFieldValue(field, values[field]);
    payload[field] = value === null || sameViewFieldValue(value, inherited[field]) ? null : value;
  }
  return payload;
}

export function isInheritOnlyPayload(payload: Record<string, unknown>): boolean {
  return VIEW_INHERITABLE_FIELDS.every(field => payload[field] === null);
}

export function isScopeBoundView(item: { definition?: { requiredScope?: string | null } | null } | null | undefined): boolean {
  return item?.definition?.requiredScope != null;
}

/**
 * A fixed built-in can never be changed, so it is not offered for management at any level. The lists that ask for a
 * choice (Per object, Per afdeling, Looproute) are ordinary rows since model A: they can be renamed, moved and
 * switched on or off like every other view.
 */
export function manageableViewRows(rows: readonly ViewsSettingsRow[]): ViewsSettingsRow[] {
  return rows.filter(row => !row.fixed);
}

export function buildCreateWithoutAncestorPayload(
  values: ViewFormValues,
  defaultPosition: number,
): Record<string, unknown> {
  return {
    key: values.key,
    name: values.name,
    avatarIconIdentifier: values.avatarIconIdentifier,
    avatarColor: values.avatarColor ?? null,
    enabled: true,
    position: defaultPosition,
    definition: values.definition,
  };
}

export function inheritedSourceForLevel(
  inherited: Pick<InheritedScopedViewItem, "sourceScope" | "contributingRowIds"> | null,
  level: ViewsSettingsLevel,
): UserPreferenceSource {
  if (!inherited) {
    return "default";
  }
  const levelIndex = VIEW_LEVEL_ORDER.indexOf(level);
  const lowerLevels = VIEW_LEVEL_ORDER.slice(0, levelIndex);
  const contributing = lowerLevels.filter(lower => inherited.contributingRowIds?.[lower] != null);
  const closest = contributing[contributing.length - 1];
  if (closest) {
    return closest;
  }
  if (VIEW_LEVEL_ORDER.indexOf(inherited.sourceScope) < levelIndex) {
    return inherited.sourceScope;
  }
  return "default";
}

export function viewSidebarLabelKeysFor(kind: ViewsSettingsKind): Record<string, string> {
  return kind === "tasks" ? TASK_VIEW_SIDEBAR_LABEL_KEYS : REPORT_VIEW_SIDEBAR_LABEL_KEYS;
}

export function resolveViewRowLabel(
  row: { key: string, name: string | null },
  kind: ViewsSettingsKind,
  t: Parameters<typeof resolveViewSidebarLabel>[2],
  te: Parameters<typeof resolveViewSidebarLabel>[3],
): string {
  return resolveViewSidebarLabel(row, viewSidebarLabelKeysFor(kind), t, te);
}

export function viewsResourceKeys(kind: ViewsSettingsKind): ResourceKeyFactory {
  return kind === "tasks" ? resourceKeys.taskViews : resourceKeys.reportViews;
}

/** Nested under the resource root, so one root invalidation also refetches the sidebar's effective list. */
export function viewsSettingsQueryKeyPrefix(kind: ViewsSettingsKind, level: ViewsSettingsLevel): readonly unknown[] {
  return [...viewsResourceKeys(kind).root, "views-settings", level, kind] as const;
}
